import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import PrepwiseLogo from '../components/PrepwiseLogo';
import { GoogleLoginButton } from '../components/ConditionalGoogleLogin';
import './Login.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // Modal state
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  const handleGoogleSuccess = async (tokenResponse: any) => {
    try {
      // Fetch user info from Google
      const userInfoResponse = await fetch(
        'https://www.googleapis.com/oauth2/v3/userinfo',
        {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        }
      );
      
      if (!userInfoResponse.ok) {
        throw new Error('Failed to fetch user info');
      }
      
      const userInfo = await userInfoResponse.json();
      
      // Login with user info
      await login({
        id: userInfo.sub,
        email: userInfo.email,
        name: userInfo.name,
        picture: userInfo.picture,
      });
      
      // Navigate based on role (will be determined in login)
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      setModal({
        isOpen: true,
        title: 'Login Failed',
        message: 'Failed to login. Please try again.',
        type: 'error',
      });
    }
  };

  const handleGoogleError = () => {
    setModal({
      isOpen: true,
      title: 'Login Failed',
      message: 'Login failed. Please try again.',
      type: 'error',
    });
  };

  return (
    <div className="login-container">
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
      
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo-container">
            <PrepwiseLogo size="large" />
            <h1>Prepwise</h1>
          </div>
          <p>Sign in to access your dashboard</p>
        </div>
        
        <div className="login-options">
          {GOOGLE_CLIENT_ID ? (
            <>
              <GoogleLoginButton
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                className="login-button google-login"
              >
                <svg className="google-icon" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </GoogleLoginButton>
              
              <div className="login-divider">
                <span>or</span>
              </div>
            </>
          ) : (
            <div className="login-notice">
              <p>Google OAuth is not configured for local development.</p>
              <Link to="/dev/login" className="dev-login-link">
                Use Dev Login Instead →
              </Link>
            </div>
          )}
        </div>

        <div className="login-footer">
          <p>
            Need to create an admin account?{' '}
            <Link to="/admin/signup" className="login-link">
              Sign up as admin
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
